import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  Calendar,
  Award,
  ExternalLink,
  Github,
  Globe,
  MessageCircle,
  UserPlus,
  Target,
  Heart,
  Quote,
  Building2,
  Star,
  ChevronRight,
  Send,
  Sparkles,
} from 'lucide-react';
import portfolioService from '@services/portfolio.service';
import profileExtendedService from '@services/profileExtended.service';
import mentorExpertiseService from '@services/mentorExpertise.service';
import connectionService from '@services/api/connection.service';
import { getImageUrl } from '@utils/imageUtils';
import Button from '@components/common/Button';
import toast from 'react-hot-toast';
import ConnectionActions from '@components/profile/ConnectionActions';
import CommentsQuestions from '@components/portfolio/CommentsQuestions';

/* ─────────────────────────────────────────────
   Tiny shared primitives
───────────────────────────────────────────── */

/** Consistent section card wrapper */
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm ${className}`}>
    {children}
  </div>
);

/** Section heading with left-accent bar */
const SectionHeading = ({ icon: Icon, iconColor = '#c33c3d', iconBg = '#fee2e2', children }) => (
  <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-5">
    <div
      className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: iconBg }}
    >
      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: iconColor }} />
    </div>
    <h2 className="text-sm sm:text-base font-bold text-gray-900">{children}</h2>
  </div>
);

/** Pill / badge */
const Badge = ({ children, variant = 'gray' }) => {
  const styles = {
    gray: 'bg-gray-50 text-gray-600 border border-gray-200',
    dark: 'bg-gray-800 text-white',
    red: 'bg-red-50 text-red-700 border border-red-200',
    blue: 'bg-blue-50 text-blue-700 border border-blue-200',
    green: 'bg-green-50 text-green-700 border border-green-200',
    orange: 'bg-orange-50 text-orange-700 border border-orange-200',
  };
  return (
    <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-semibold ${styles[variant]}`}>
      {children}
    </span>
  );
};

/** Empty state */
const Empty = ({ icon: Icon = Award, text = 'Nothing added yet' }) => (
  <div className="flex flex-col items-center justify-center py-8 sm:py-10 gap-2">
    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-50 rounded-full flex items-center justify-center">
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300" />
    </div>
    <p className="text-xs text-gray-400 text-center px-4">{text}</p>
  </div>
);

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const StudentDetails = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [skills, setSkills] = useState([]);
  const [personalityTraits, setPersonalityTraits] = useState([]);
  const [careerDomains, setCareerDomains] = useState([]);
  const [mentorExpertise, setMentorExpertise] = useState([]);
  const [userStats, setUserStats] = useState({ followersCount: 0, followingCount: 0, connectionsCount: 0 });
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    fetchStudentDetails();
    fetchCareerDomains();
    fetchUserStats();
    fetchConnectionStatus();
  }, [studentId]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await portfolioService.getPortfolioById(studentId);
      if (response.success) {
        const studentData = response?.data?.data;
        setStudent(studentData);
        if (studentData?.id) {
          fetchExperiences(studentData.id);
          fetchSkills(studentData.id);
          fetchPersonalityTraits(studentData.id);
          if (studentData?.role === 'mentor') fetchMentorExpertise();
        }
      } else {
        toast.error('Failed to load portfolio');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
      toast.error('Failed to load portfolio');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchExperiences = async (userId) => {
    try {
      const response = await profileExtendedService.getExperiencesByUserId(userId);
      const data = response.data?.data || response.data || [];
      setExperiences(Array.isArray(data) ? data : []);
    } catch { setExperiences([]); }
  };

  const fetchSkills = async (userId) => {
    try {
      const response = await profileExtendedService.getSkillsByUserId(userId);
      const data = response.data?.data || response.data || [];
      setSkills(Array.isArray(data) ? data : []);
    } catch { setSkills([]); }
  };

  const fetchPersonalityTraits = async (userId) => {
    try {
      const response = await profileExtendedService.getPersonalityTraitsByUserId(userId);
      const data = response.data?.data || response.data || [];
      setPersonalityTraits(Array.isArray(data) ? data : []);
    } catch { setPersonalityTraits([]); }
  };

  const fetchCareerDomains = async () => {
    try {
      const response = await portfolioService.getCareerDomains();
      const data = response.data?.data || response.data || [];
      setCareerDomains(Array.isArray(data) ? data : []);
    } catch { setCareerDomains([]); }
  };

  const fetchMentorExpertise = async () => {
    try {
      const response = await mentorExpertiseService.getMentorExpertise(studentId);
      if (response.success) setMentorExpertise(response?.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch mentor expertise:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await connectionService.getUserStats(studentId);
      if (response.success) {
        setUserStats(response.data || { followersCount: 0, followingCount: 0, connectionsCount: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      setStatusLoading(true);
      const response = await connectionService.getConnectionStatus(studentId);
      if (response.success) {
        setConnectionStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch connection status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleConnectionStatusChange = (newStatus) => {
    setConnectionStatus(newStatus);
  };

  const getInitials = () => {
    if (student?.firstName && student?.lastName)
      return `${student.firstName[0]}${student.lastName[0]}`.toUpperCase();
    return student?.email?.charAt(0).toUpperCase() || 'U';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#c33c3d] border-t-transparent" />
      </div>
    );
  }

  /* ── Not found ── */
  if (!student) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">Profile not found</h2>
          <Button onClick={() => navigate('/dashboard')}>Back to Discovery</Button>
        </div>
      </div>
    );
  }

  const fullName = student.firstName && student.lastName
    ? `${student.firstName} ${student.lastName}`
    : student.email;

  const careerDomain = careerDomains.find(
    (d) => d.id === student.careerFocusIndustryId || d.id === parseInt(student.industryId)
  );

  /* ─────────────────────────────────────────
     RENDER
  ───────────────────────────────────────── */
  return (
    <div className="w-full min-h-screen bg-gray-50/70 pb-8 sm:pb-12">

      {/* ── Back nav ── */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-gray-100 px-3 sm:px-4 py-2.5 sm:py-3">
        <button
          onClick={() => navigate('/students')}
          className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Discovery
        </button>
      </div>

      <div className="max-w-8xl mx-auto px-3 sm:px-4 pt-4 sm:pt-6 space-y-4 sm:space-y-5">

        {/* ══════════════════════════════════════
            HERO — Profile Header
        ══════════════════════════════════════ */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="relative px-4 py-5 sm:px-6 sm:py-6">
            {/* Decorative top accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500"></div>
            
            {/* Content */}
            <div className="relative">
              <div className="flex flex-col md:flex-row items-center md:items-center gap-4 sm:gap-6 mb-4 sm:mb-6 text-center md:text-left">
                {/* Profile Avatar */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    {student.profilePicture ? (
                      <>
                        <img
                          src={getImageUrl(student.profilePicture)}
                          alt={fullName}
                          className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full object-cover border-4 border-gray-200 shadow-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div
                          className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center border-4 border-gray-200 shadow-lg"
                          style={{ display: 'none' }}
                        >
                          <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">{getInitials()}</span>
                        </div>
                      </>
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center border-4 border-gray-200 shadow-lg">
                        <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">{getInitials()}</span>
                      </div>
                    )}
                    {/* Online dot */}
                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-green-400 rounded-full border-2 border-white shadow-sm" />
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex-1 min-w-0 w-full">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">{fullName}</h1>
                  
                  {/* Badges */}
                  <div className="flex flex-wrap justify-center md:justify-start gap-1.5 sm:gap-2 mb-3">
                    {student.careerStage && (
                      <span className="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                        {student.careerStage.toUpperCase()}
                      </span>
                    )}
                    {student.role && (
                      <span className="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-300">
                        {student.role.charAt(0).toUpperCase() + student.role.slice(1)}
                      </span>
                    )}
                    {careerDomain && (
                      <span className="inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-300">
                        {careerDomain.name}
                      </span>
                    )}
                  </div>

                  {/* Meta row */}
                  <div className="flex flex-wrap justify-center md:justify-start items-center gap-x-3 gap-y-1.5 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3">
                    {student.city && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                        {student.city}
                      </span>
                    )}
                    {student.institution && (
                      <span className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                        {student.institution}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-gray-700 font-medium">
                      <span className="font-bold text-gray-900">{userStats.followersCount}</span> Followers
                    </span>
                    <span className="flex items-center gap-1 text-gray-700 font-medium">
                      <span className="font-bold text-gray-900">{userStats.connectionsCount}</span> Connections
                    </span>
                  </div>

                  {/* Bio / career goal */}
                  {student.careerGoal && (
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-left">
                      {student.careerGoal}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-center gap-2 w-full md:w-auto md:self-start shrink-0">
                  <ConnectionActions userId={studentId} onStatusChange={handleConnectionStatusChange} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Required Message */}
        {!statusLoading && !connectionStatus?.isOwnProfile && !connectionStatus?.isConnected && (
          <Card className="p-4 sm:p-6">
            <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-50 flex items-center justify-center mb-3 sm:mb-4">
                <UserPlus className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Connect to View Full Profile</h3>
              <p className="text-xs sm:text-sm text-gray-600 max-w-md">
                Send a connection request to view this user's complete portfolio including experience, skills, awards, and more.
              </p>
            </div>
          </Card>
        )}

        {/* ══════════════════════════════════════
            TWO-COLUMN LAYOUT starts here
            Left: main content
            Right: sidebar (skills, traits, career, links)
            Only show if connected or own profile
        ══════════════════════════════════════ */}
        {(statusLoading || connectionStatus?.isOwnProfile || connectionStatus?.isConnected) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">

          {/* ── LEFT column (2/3) ── */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-5">

            {/* About Section - For Mentors */}
            {student.role === 'mentor' && student.careerGoal && (
              <Card className="p-4 sm:p-6">
                <SectionHeading icon={Quote} iconBg="#fee2e2" iconColor="#c33c3d">
                  About
                </SectionHeading>
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  {student.careerGoal}
                </p>
              </Card>
            )}

            {/* Mentor Expertise */}
            {student.role === 'mentor' && mentorExpertise.length > 0 && (
              <Card className="p-4 sm:p-6">
                <SectionHeading icon={Sparkles} iconBg="#fee2e2" iconColor="#c33c3d">
                  Areas of Expertise
                </SectionHeading>
                <div className="space-y-3 sm:space-y-4">
                  {mentorExpertise.map((expertise, index) => (
                    <div key={expertise.id || index} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-red-50/60 rounded-xl border border-red-100">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-xs sm:text-sm font-bold text-gray-900">{expertise.jobTitle}</h3>
                          <Badge variant="red">{expertise.industryName}</Badge>
                        </div>
                        {expertise.expertiseDescription && (
                          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                            {expertise.expertiseDescription}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Experience */}
            <Card className="p-4 sm:p-6">
              <SectionHeading icon={Briefcase} iconBg="#fee2e2" iconColor="#c33c3d">
                Experience
              </SectionHeading>

              {experiences.length === 0 ? (
                <Empty icon={Briefcase} text="No experience added yet" />
              ) : (
                <div className="space-y-1">
                  {experiences.map((exp, idx) => (
                    <div key={exp.id}>
                      <div className="flex gap-3 sm:gap-4 py-3 sm:py-4">
                        {/* Timeline dot + line */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-red-50 flex items-center justify-center border border-red-200">
                            <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                          </div>
                          {idx < experiences.length - 1 && (
                            <div className="w-px flex-1 bg-red-100 mt-2" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0 pb-2">
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                            <div>
                              <h3 className="text-xs sm:text-sm font-bold text-gray-900">{exp.jobTitle}</h3>
                              <p className="text-xs sm:text-sm font-medium text-gray-600">{exp.company}</p>
                            </div>
                            {exp.isCurrent && <Badge variant="green">Current</Badge>}
                          </div>

                          <div className="flex flex-wrap gap-2 sm:gap-3 text-[11px] sm:text-xs text-gray-400 mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(exp.startDate)} – {exp.isCurrent ? 'Present' : formatDate(exp.endDate)}
                            </span>
                            {exp.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {exp.location}
                              </span>
                            )}
                          </div>

                          {exp.description && (
                            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{exp.description}</p>
                          )}
                        </div>
                      </div>
                      {idx < experiences.length - 1 && (
                        <div className="border-t border-gray-50 ml-11 sm:ml-13" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Awards & Honors */}
            <Card className="p-4 sm:p-6">
              <SectionHeading icon={Award} iconBg="#fee2e2" iconColor="#c33c3d">
                Awards & Honors
              </SectionHeading>

              {student?.awards && student.awards.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  {student.awards.map((award, index) => (
                    <div key={index} className="flex gap-3 p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                        <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xs sm:text-sm font-bold text-gray-900 capitalize mb-0.5">
                          {award.type || 'Achievement'}
                        </h3>
                        {award.note && (
                          <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">{award.note}</p>
                        )}
                        {award.image && (
                          <img
                            src={getImageUrl(award.image)}
                            alt={award.type || 'Award'}
                            className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-lg mt-2"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty icon={Award} text="No awards or honors added yet" />
              )}
            </Card>

            {/* Comments & Questions */}
            <CommentsQuestions 
              portfolioUserId={studentId} 
              isOwnProfile={connectionStatus?.isOwnProfile || false}
            />
          </div>

          {/* ── RIGHT sidebar (1/3) ── */}
          <div className="space-y-4 sm:space-y-5">

            {/* External Links */}
            <Card className="p-4 sm:p-5">
              <SectionHeading icon={ExternalLink} iconBg="#e6f1fb" iconColor="#185fa5">
                Links
              </SectionHeading>
              <div className="space-y-2">
                {[
                  { icon: Globe, label: 'Portfolio', color: 'text-blue-500' },
                  { icon: Github, label: 'GitHub', color: 'text-gray-800' },
                ].map(({ icon: Icon, label, color }) => (
                  <button
                    key={label}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all text-left group"
                  >
                    <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                    <span className="text-xs sm:text-sm font-medium text-gray-700 flex-1">{label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-400" />
                  </button>
                ))}
              </div>
            </Card>

            {/* Skills - Only show for students, not mentors */}
            {student.role !== 'mentor' && (
            <Card className="p-4 sm:p-5">
              <SectionHeading icon={Award} iconBg="#fee2e2" iconColor="#c33c3d">
                Skills
              </SectionHeading>
              {skills.length === 0 ? (
                <Empty icon={Award} text="No skills added" />
              ) : (
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {skills.map((skill) => (
                    <span
                      key={skill.id}
                      className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg text-[11px] sm:text-xs font-medium hover:bg-red-100 transition-colors cursor-default"
                    >
                      {skill.skillName}
                    </span>
                  ))}
                </div>
              )}
            </Card>
            )}

            {/* Career Focus */}
            <Card className="p-4 sm:p-5">
              <SectionHeading icon={Target} iconBg="#fee2e2" iconColor="#c33c3d">
                Career Focus
              </SectionHeading>
              {careerDomain ? (
                <div className="flex items-center gap-2 px-3 py-2 sm:py-2.5 bg-red-50 border border-red-200 rounded-xl">
                  <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-semibold text-gray-800">{careerDomain.name}</span>
                </div>
              ) : (
                <Empty icon={Target} text="No career focus set" />
              )}
            </Card>

            {/* Personality Traits - Only show for students, not mentors */}
            {student.role !== 'mentor' && (
            <Card className="p-4 sm:p-5">
              <SectionHeading icon={Heart} iconBg="#fee2e2" iconColor="#c33c3d">
                Personality Traits
              </SectionHeading>
              {personalityTraits.length === 0 ? (
                <Empty icon={Heart} text="No traits added" />
              ) : (
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {personalityTraits.map((trait) => (
                    <span
                      key={trait.id}
                      className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-red-50 border border-red-200 text-red-700 rounded-lg text-[11px] sm:text-xs font-medium"
                    >
                      {trait.trait?.name || trait.name || 'Trait'}
                    </span>
                  ))}
                </div>
              )}
            </Card>
            )}

          </div>
          {/* end sidebar */}
        </div>
        )}
        {/* end two-column */}

      </div>
      {/* end max-w container */}
    </div>
  );
};

export default StudentDetails;